import React from 'react'

type ListPaginationProps = {
  page: number
  pageSize: number
  totalRecords: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  pageSizes?: number[]
  disabled?: boolean
  className?: string
}

/** Shared presentation for list pagination. Data loading and filtering stay with each page. */
const ListPagination: React.FC<ListPaginationProps> = ({
  page,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageSizes = [10, 25, 50, 100],
  disabled = false,
  className = '',
}) => {
  const safePageSize = Math.max(1, pageSize || 1)
  const total = Math.max(0, totalRecords || 0)
  const totalPages = Math.ceil(total / safePageSize)
  const currentPage = totalPages ? Math.min(Math.max(1, page), totalPages) : 1
  const firstPage = Math.max(1, currentPage - 2)
  const lastPage = Math.min(totalPages, currentPage + 2)
  const pages = Array.from({length: Math.max(0, lastPage - firstPage + 1)}, (_, i) => firstPage + i)

  return (
    <div className={`d-flex flex-wrap align-items-center justify-content-between gap-3 ${className}`.trim()}>
      <span className='text-muted small' aria-live='polite'>
        {total === 0 ? '0 records' : `Showing ${(currentPage - 1) * safePageSize + 1}–${Math.min(currentPage * safePageSize, total)} of ${total} records`}
      </span>
      <div className='d-flex flex-wrap align-items-center gap-2'>
        {onPageSizeChange && (
          <label className='d-flex align-items-center gap-2 text-muted small mb-0'>
            Rows
            <select
              className='form-select form-select-sm w-auto'
              aria-label='Rows per page'
              value={safePageSize}
              disabled={disabled}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        )}
        <button type='button' className='btn btn-sm btn-light' disabled={disabled || currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
          Previous
        </button>
        {pages.map((pageNumber) => (
          <button
            type='button'
            key={pageNumber}
            className={`btn btn-sm ${pageNumber === currentPage ? 'btn-primary' : 'btn-light'}`}
            aria-current={pageNumber === currentPage ? 'page' : undefined}
            aria-label={`Page ${pageNumber}`}
            disabled={disabled}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber}
          </button>
        ))}
        <button type='button' className='btn btn-sm btn-light' disabled={disabled || currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
          Next
        </button>
      </div>
    </div>
  )
}

export default ListPagination
